import { ArsenalArmy, Nullable } from '@Models';
import {
  ArsenalArmyRepository,
  RequestArsenalArmySuccess,
} from '@Repository/arsenal';
import { inject, injectable } from 'inversify';
import { TYPES } from '@Types';
import { Locale } from '@Repository/locale';
import { RequestAction } from '@Repository/RequestAction';
import { ArsenalArmyCreateParams } from '@Builder/myArsenal/army/new/validate';
import { Result } from '@Redux/type';
import { DeleteRepository } from '@Repository/DeleteRepository';
import { ArmyCategoryAction } from '@Redux/actions';
import { IEditableEntityAction } from '@Redux/IEditableEntityAction';
import { PayloadAction } from '@reduxjs/toolkit';
import arsenalArmySlice from '@Redux/reducers/arsenal/arsenal-army.slice';

@injectable()
export class MyArsenalAction extends IEditableEntityAction<
  string,
  ArsenalArmy,
  RequestArsenalArmySuccess
> {
  @inject(TYPES.Actions.ArmyCategoryAction)
  private _armyCategoryAction!: ArmyCategoryAction;
  @inject(TYPES.Repository.ArsenalArmyRepository)
  private _repository!: ArsenalArmyRepository;
  @inject(TYPES.Locale) private _locale!: Locale;

  public create =
    (values: ArsenalArmyCreateParams): Result<void> =>
    async (dispatch) => {
      dispatch(this.areLoading(true));

      const army: ArsenalArmy = {
        id: '',
        name: values.name,
        armyCategoryId: values.armyCategoryId,
        updatedAt: 0,
        logo: null,
        largeLogo: null,
        units: [],
        steps: [],
      } satisfies ArsenalArmy as ArsenalArmy;

      const response = await this._repository.create(army, { locale: null });

      if (this.handleFailure(response, dispatch)) return;

      const success = response as RequestArsenalArmySuccess;
      window.location.href = `/${this._locale.getlocale()}/arsenal/armies/${
        success.payload.id
      }`;
    };

  public getByArmyCategoryId =
    (armyCategoryId: number): Result<void> =>
    async (dispatch) => {
      dispatch(this.areLoading(true));

      const response = await this._repository.getByArmyCategoryId(
        armyCategoryId,
        { locale: null },
      );

      if (this.handleFailure(response, dispatch)) return;

      const success = response as RequestArsenalArmySuccess;
      dispatch(this.fetchDataSuccess(success.payload));
      dispatch(this.areLoading(false));
    };

  getDeleteRepository(): DeleteRepository<string> {
    return this._repository;
  }

  protected fetchDataSuccess(item: ArsenalArmy): PayloadAction<ArsenalArmy> {
    return arsenalArmySlice.actions.setArsenalArmy(item);
  }

  protected cacheDataSuccess(item: ArsenalArmy): PayloadAction<ArsenalArmy> {
    return arsenalArmySlice.actions.setArsenalArmy(item);
  }

  protected haveError(message: string): PayloadAction<string> {
    return arsenalArmySlice.actions.setError(message);
  }

  protected areLoading(bool: boolean): PayloadAction<boolean> {
    return arsenalArmySlice.actions.setLoading(bool);
  }

  protected async getCachedItem({
    entityId,
    locale,
  }: {
    entityId: string;
    locale: Nullable<string>;
  }): Promise<Nullable<ArsenalArmy>> {
    return await this._repository.getCached(entityId, {
      locale: locale ?? null,
    });
  }

  protected async getItem({
    entityId,
    locale,
  }: {
    entityId: string;
    locale: Nullable<string>;
  }): Promise<RequestAction> {
    return await this._repository.get(entityId, { locale: locale ?? null });
  }

  protected subFetchDataBefore =
    ({ entity }: { entity: ArsenalArmy }): Result<void> =>
    (dispatch) => {
      dispatch(
        this._armyCategoryAction.fetchData({ entityId: entity.armyCategoryId }),
      );
    };

  protected subFetchDataAfter =
    ({
      entity,
      cachedItem,
    }: {
      entity: ArsenalArmy;
      cachedItem: Nullable<ArsenalArmy>;
    }): Result<void> =>
    (dispatch) => {
      if (!cachedItem || cachedItem?.armyCategoryId != entity.armyCategoryId) {
        dispatch(
          this._armyCategoryAction.fetchData({
            entityId: entity.armyCategoryId,
          }),
        );
      }
    };
}
